import {
  ModalController,
  IonSelectOption,
  IonItemDivider,
  IonFabButton,
  IonTextarea,
  IonToolbar,
  IonButtons,
  IonContent,
  IonButton,
  IonSelect,
  IonHeader,
  IonTitle,
  IonIcon,
  IonItem,
  IonList,
  IonText,
  IonFab,
} from '@ionic/angular/standalone';
import { AbstractControl, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { input, Component, ChangeDetectorRef, ChangeDetectionStrategy, signal, inject } from '@angular/core';
import { chevronBack, document, stop, micOutline, radioButtonOff } from 'ionicons/icons';
import { toSignal } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { addIcons } from 'ionicons';

import { MathPipe } from '@app/pipes/math.pipe';
// import { Lang } from '@app/interfaces/language.interface';
import { ToastService } from '@app/services/toast.service';
import { StorageService } from '@app/services/storage.service';
import { ContactsService } from '@app/services/contacts.service';
import { SmsMessagesService } from '@app/services/sms-messages.service';
// import { SpeechToTextService } from '@app/services/speech-to-text.service';
import { MessageTemplateService } from '@app/services/message-template.service';
import { OurGsmNumberInterface } from '@app/interfaces/our-gsm-number.interface';
import { OfflineDialCodesService } from '@app/services/offline-dial-codes.service';
import { ComposeMessageInterface, SenderInterface } from '@app/interfaces/message.interface';
import { SelectContactComponent } from '@app/components/select-contact/select-contact.component';

@Component({
  selector: 'app-compose-message',
  templateUrl: 'compose-message.page.html',
  styleUrls: ['./compose-message.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SelectContactComponent,
    ReactiveFormsModule,
    IonSelectOption,
    IonItemDivider,
    FormsModule,
    IonTextarea,
    IonToolbar,
    IonButtons,
    IonContent,
    IonHeader,
    IonButton,
    IonSelect,
    MathPipe,
    IonTitle,
    IonIcon,
    IonText,
    IonItem,
    IonList,
  ],
})
export class ComposeMessagePage {
  private offlineDialCodesSrv = inject(OfflineDialCodesService);
  private msTemplateSrv = inject(MessageTemplateService);
  private smsMessagesSrv = inject(SmsMessagesService);
  private contactsSrv = inject(ContactsService);
  private modalCtrl = inject(ModalController);
  private storage = inject(StorageService);
  private toastSrv = inject(ToastService);
  private cdr = inject(ChangeDetectorRef);

  // Lang = Lang;
  messageForm: FormGroup;
  ourGsmNumbers = toSignal(this.offlineDialCodesSrv.getOurGsmNumber(), { requireSync: true });
  contacts = toSignal(this.contactsSrv.contacts$);
  // isRecording = toSignal(this.speechSrv.isRecording$);

  isSelectContact = input<boolean>(false);
  contact = input<SenderInterface>();
  message = input<string>('');
  prevMsg = signal('');
  orderedGsmNumbers = signal<OurGsmNumberInterface[]>([]);

  constructor() {
    console.log('Init ComposeMessagePage');
    addIcons({ chevronBack, document, radioButtonOff, micOutline, stop });
    this.messageForm = new FormGroup({
      sender: new FormControl<OurGsmNumberInterface | null>(null, [Validators.required]),
      recipient: new FormControl<SenderInterface | null>(null, [Validators.required]),
      msg: new FormControl<string>('', [Validators.required, Validators.maxLength(918)]),
    });
    // this.speechSrv.transcript$.subscribe((transcript) => {
    //   this.msg.markAsDirty();
    //   const currentMsg = this.prevMsg() ? `${this.prevMsg()}. ${transcript}` : transcript;
    //   this.msg.setValue(currentMsg);
    // });
  }

  async ionViewDidEnter() {
    // await this.speechSrv.init();
    this.msg.setValue(this.message() ?? '');
    if (this.contact()) {
      this.setRecipient(this.contact());
      await this.getLastSmsDraftFromStorage(this.contact()!.PHONE);
    }
    this.cdr.markForCheck();
  }

  ionViewWillLeave() {
    // if (this.isRecording()) {
    //   this.speechSrv.stopRecording();
    // }
  }

  get sender(): AbstractControl {
    return this.messageForm.get('sender') as AbstractControl;
  }

  get recipient(): AbstractControl {
    return this.messageForm.get('recipient') as AbstractControl;
  }

  get msg(): AbstractControl {
    return this.messageForm.get('msg') as AbstractControl;
  }

  private async getLastSmsDraftFromStorage(phone: string) {
    const smsDraft = await this.storage.get(`smsDraft_${phone}`);
    if (smsDraft) {
      this.msg.setValue(smsDraft);
    }
  }

  private updateOrderedGsmAndSender(phone: string): void {
    const ordered = this.sortByPhonePattern(this.ourGsmNumbers(), phone);
    this.orderedGsmNumbers.set(ordered);
    this.sender.setValue(ordered[0] ?? null);
  }

  private sortByPhonePattern(items: OurGsmNumberInterface[], phoneNumber: string): OurGsmNumberInterface[] {
    const patternMatches = (pattern: string, phone: string) => {
      try {
        const regex = new RegExp(pattern);
        return regex.test(phone);
      } catch {
        return false;
      }
    };

    return [...items].sort((a, b) => {
      const aMatch = patternMatches(a.PATTERN, phoneNumber) ? 0 : 1;
      const bMatch = patternMatches(b.PATTERN, phoneNumber) ? 0 : 1;
      return aMatch - bMatch;
    });
  }

  getTemplate() {
    this.msTemplateSrv
      .getMessageTemplate(this.recipient.value.LANGUAGE, this.recipient.value.GENDER, this.recipient.value.VIP_FAMILY)
      .subscribe((resp) => {
        const formatted = this.formatMessage(`${resp.GREETING}\n\n${resp.ENDING}`, this.recipient.value);
        this.msg.setValue(formatted);
      });
  }

  formatMessage(message: string, contact: ComposeMessageInterface) {
    return message.replace('%FIRSTNAME%', contact.FIRSTNAME || '').replace('%FAMILYNAME%', contact.FAMILYNAME || '');
  }

  sendMessage() {
    this.smsMessagesSrv
      .sendSMS(this.sender.value.GSM_NUMBER, this.recipient.value.PHONE, this.msg.value)
      .subscribe(() => {
        this.toastSrv.showToast('Message is sent');
        this.dismiss('sent');
      });
  }

  dismiss(data?: string) {
    const { PHONE } = this.recipient.value ?? {};
    if (PHONE) {
      if (data !== 'sent' && this.msg.dirty) {
        this.storage.set(`smsDraft_${PHONE}`, this.msg.value);
      } else if (data === 'sent') {
        this.storage.remove(`smsDraft_${PHONE}`);
      }
    }
    this.modalCtrl.dismiss(data);
  }

  // speechToText() {
  //   if (this.isRecording()) {
  //     this.speechSrv.stopRecording();
  //   } else {
  //     this.prevMsg.set(this.msg.value);
  //     this.speechSrv.startRecording();
  //   }
  // }

  // changeLanguage(event: CustomEvent) {
  //   this.speechSrv.setLanguage(event.detail.value);
  // }

  onGsmNumberChange(event: CustomEvent) {
    this.sender.setValue(event.detail.value);
  }

  setRecipient(val?: SenderInterface) {
    this.recipient.setValue(val);
    this.updateOrderedGsmAndSender(this.recipient.value.PHONE);
  }
}
